<script setup lang="ts">
import EditDataSectionItemMenu from '@/components/EditDataSectionItemMenu.vue'
import Dialog from '@/components/Dialog.vue'
import Button from '@/components/Button.vue'
import Input from '@/components/Input.vue'
import { FieldArray, FieldEntry } from 'vee-validate'
import { reactive, ref, watch, computed } from 'vue'
import { useDisplay } from 'vuetify'

interface EditDataSectionProps {
  title: string
  actionLabel: string
  section: string
  defaultLabel: string
  defaultItemValue: unknown
  inlineActions?: boolean
  requiredFirstItem?: boolean
}

const props = withDefaults(defineProps<EditDataSectionProps>(), {
  inlineActions: true,
  requiredFirstItem: false,
})

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

type ItemValue = { label?: string | null; main?: boolean | null }
function isItemValue(v: unknown): v is ItemValue {
  return v != null && typeof v === 'object'
}

type AddressFieldValue = { label?: string }

const emits = defineEmits([
  'close',
  'save',
  'update',
  'changeModal',
  'edit-label',
])

const modals = reactive({ editLabel: false })
const selectedField = ref<number>()
const labelBeingEdited = ref(props.defaultLabel)

function openEditLabelDialog(field: FieldEntry, idx: number) {
  modals.editLabel = true
  labelBeingEdited.value =
    (field.value as AddressFieldValue)?.label ?? props.defaultLabel
  selectedField.value = idx
}

function saveLabel() {
  emits('edit-label', {
    idx: selectedField.value,
    label: labelBeingEdited.value,
  })
  selectedField.value = undefined
  labelBeingEdited.value = ''
  modals.editLabel = false
}

const internalIsOpen = ref(modals.editLabel ?? false)

watch(
  () => modals.editLabel,
  (val) => (internalIsOpen.value = val ?? false)
)

watch(internalIsOpen, (val) => {
  if (!val) emits('close')
})

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getNextIndexFromFields(fields: FieldEntry[]): number {
  let maxIndex = 0
  const escapedLabel = escapeRegExp(props.defaultLabel)
  const regex = new RegExp(`^${escapedLabel}\\s+(\\d+)`, 'i')

  fields.forEach((field, idx) => {
    const value = field.value as ItemValue | undefined

    if (value && typeof value.label === 'string') {
      const match = value.label.match(regex)
      if (match) {
        const parsed = Number.parseInt(match[1], 10)
        if (!Number.isNaN(parsed) && parsed > maxIndex) {
          maxIndex = parsed
        }
        return
      }
    }

    const fallbackIndex = idx + 1
    if (fallbackIndex > maxIndex) {
      maxIndex = fallbackIndex
    }
  })

  return maxIndex + 1
}

function handleAddItem(
  fields: FieldEntry[],
  push: (value: unknown) => void,
  value?: unknown
) {
  let base: Record<string, unknown> = {}

  if (props.defaultItemValue && typeof props.defaultItemValue === 'object') {
    base = { ...(props.defaultItemValue as Record<string, unknown>) }
  }

  if (value && typeof value === 'object') {
    base = { ...base, ...(value as Record<string, unknown>) }
  }

  const nextIndex = getNextIndexFromFields(fields)
  ;(base as ItemValue).label = `${props.defaultLabel} ${nextIndex}`

  if (fields.length === 0) {
    ;(base as ItemValue).main = true
  } else {
    ;(base as ItemValue).main = false
  }

  push(base)
}

function isMain(field: FieldEntry): boolean {
  return isItemValue(field.value) && !!field.value.main
}

function getMainIndex(fields: FieldEntry[]): number {
  let mainIdx = -1

  fields.forEach((field, idx) => {
    if (isMain(field) && mainIdx === -1) {
      mainIdx = idx
    }
  })

  if (mainIdx === -1) {
    return fields.length > 0 ? 0 : -1
  }

  return mainIdx
}

function getDisplayLabel(
  field: FieldEntry,
  idx: number,
  fields: FieldEntry[]
): string {
  const baseLabel =
    isItemValue(field.value) && field.value.label
      ? field.value.label
      : `${props.defaultLabel} ${idx + 1}`

  const isRequiredMain = props.requiredFirstItem && idx === getMainIndex(fields)

  return isRequiredMain ? `${baseLabel} *` : baseLabel
}

function handleSetMain(fields: FieldEntry[], idxMain: number) {
  fields.forEach((field, idx) => {
    if (!isItemValue(field.value)) return
    field.value.main = idx === idxMain
  })
}
</script>

<template>
  <FieldArray v-slot="{ fields, push, remove, move }" :name="props.section">
    <v-container fluid class="pa-0">
      <slot
        name="header"
        :push="(val?: unknown) => handleAddItem(fields, push, val)"
      >
        <v-row
          v-if="!isMobile"
          class="mb-2"
          justify="space-between"
          align="center"
          no-gutters
        >
          <v-col cols="auto">
            <p class="text-h6 font-weight-medium mb-0">{{ props.title }}</p>
          </v-col>
          <v-col cols="auto">
            <Button
              :id="`${props.section}-add-new-item-button`"
              color="primary"
              class="primaryOutline"
              style="min-width: 180px; height: 44px; padding: 0 16px"
              @click="() => handleAddItem(fields, push)"
            >
              {{ props.actionLabel }}
            </Button>
          </v-col>
        </v-row>
      </slot>

      <v-row
        v-for="(field, idx) in fields"
        :id="`edit-menu-${props.section}-${idx}`"
        :key="field.key"
        no-gutters
        class="mb-3"
      >
        <v-col cols="12">
          <v-row align="center" justify="space-between" no-gutters>
            <v-col
              v-if="props.section !== 'emails'"
              cols="auto"
              class="d-flex align-center"
            >
              <span
                v-if="idx === getMainIndex(fields)"
                class="main-star"
                aria-hidden="true"
              >
                ★
              </span>

              <p class="label_style">
                {{ getDisplayLabel(field, idx, fields) }}
              </p>
            </v-col>

            <v-col
              v-if="props.section === 'emails' && idx === 0"
              cols="12"
              class="email-spacer"
            ></v-col>

            <v-col v-if="!props.inlineActions" cols="auto">
              <EditDataSectionItemMenu
                :id="idx"
                :section="props.section"
                :move="move"
                :push="(val?: unknown) => handleAddItem(fields, push, val)"
                :remove="() => remove(idx)"
                :field-length="fields.length"
                :is-main="isMain(field)"
                @edit-label="openEditLabelDialog(field, idx)"
                @set-main="() => handleSetMain(fields, idx)"
              />
            </v-col>
          </v-row>

          <template v-if="props.inlineActions">
            <v-row align="start" no-gutters class="eds-inline-row">
              <v-col class="pr-2">
                <slot
                  :idx="idx"
                  :field="field"
                  :label="getDisplayLabel(field, idx, fields)"
                  :is-main="idx === getMainIndex(fields)"
                />
              </v-col>

              <v-col cols="auto" class="eds-inline-menu pl-2">
                <EditDataSectionItemMenu
                  :id="idx"
                  :section="props.section"
                  :move="move"
                  :push="(val?: unknown) => handleAddItem(fields, push, val)"
                  :remove="() => remove(idx)"
                  :field-length="fields.length"
                  :is-main="isMain(field)"
                  @edit-label="openEditLabelDialog(field, idx)"
                  @set-main="() => handleSetMain(fields, idx)"
                />
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <slot
              :idx="idx"
              :field="field"
              :label="getDisplayLabel(field, idx, fields)"
              :is-main="idx === getMainIndex(fields)"
            />
          </template>
        </v-col>
      </v-row>

      <div v-if="isMobile" class="d-flex mt-2 justify-center">
        <Button
          :id="`${props.section}-add-new-item-button`"
          color="primary"
          class="primaryOutline"
          style="width: 100%; height: 44px"
          @click="() => handleAddItem(fields, push)"
        >
          {{ props.actionLabel }}
        </Button>
      </div>
    </v-container>
  </FieldArray>

  <Dialog
    v-model="internalIsOpen"
    :title="$t('corporate.edit.editLabelTitle')"
    @close="modals.editLabel = false"
  >
    <div class="w-100 label-input-with-counter my-4">
      <Input
        id="edit-label-dialog-input"
        v-model="labelBeingEdited"
        :label="$t('corporate.edit.editLabelField')"
        maxlength="150"
      />
    </div>

    <template #actions>
      <Button
        id="cancel-data-button"
        :class="[!isMobile ? 'tertiaryFlat' : 'primaryOutline']"
        @click="modals.editLabel = false"
      >
        {{ $t('general.cancel') }}
      </Button>

      <Button
        id="edit-label-dialog-button"
        type="button"
        class="primaryFlat"
        @click="saveLabel"
      >
        {{ $t('general.save') }}
      </Button>
    </template>
  </Dialog>
</template>

<style scoped>
.eds-inline-row {
  align-items: flex-start;
}

.eds-inline-menu {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 32px;
  margin-top: 8px;
}

.eds-inline-row :deep(.v-field) {
  margin-right: 0;
}

.label_style {
  color: var(--text-text--secondary, var(--black-0-6, rgba(0, 0, 0, 0.6)));
  font-family: var(--fontFamilies-roboto, Roboto);
  font-size: var(--fontSize-42, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--lineHeights-42, 28px);
  letter-spacing: 0.024px;
}

.email-spacer {
  height: 12px;
}

.eds-inline-menu {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  min-width: 32px;
  padding-top: 4px;
}

.main-star {
  color: #8dc63f;
  font-size: 14px;
  line-height: 1;
  margin-right: 6px;
  transform: translateY(-1px);
  display: inline-flex;
}

.label-input-with-counter {
  position: relative;
  width: 100%;
}

.label-char-counter {
  position: absolute;
  right: 12px;
  top: 48px;
  font-size: 12px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
</style>
